import { useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";

const EMPTY_PAIR = { ifcX: "", ifcY: "" };

/**
 * Two-point calibration between a drawing (PDF points) and the federated IFC
 * model. The user picks two points on the sheet, then types the matching IFC
 * X/Y for each. Promang derives scale, rotation and offset from that pair.
 */
export const DrawingIfcAlignmentDialog = ({
  t,
  open,
  points = [],
  saving,
  onClose,
  onSave,
}) => {
  const [pairs, setPairs] = useState([{ ...EMPTY_PAIR }, { ...EMPTY_PAIR }]);
  const [zOffset, setZOffset] = useState("0");
  const [storeyName, setStoreyName] = useState("");

  useEffect(() => {
    if (open) {
      setPairs([{ ...EMPTY_PAIR }, { ...EMPTY_PAIR }]);
      setZOffset("0");
      setStoreyName("");
    }
  }, [open]);

  if (!open) return null;

  const setPair = (index, key, value) =>
    setPairs((current) =>
      current.map((pair, i) => (i === index ? { ...pair, [key]: value } : pair))
    );

  const numeric = (value) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const complete =
    points.length === 2 &&
    pairs.every((pair) => numeric(pair.ifcX) !== null && numeric(pair.ifcY) !== null);

  const handleSave = () => {
    if (!complete) return;
    onSave({
      z_offset: numeric(zOffset) ?? 0,
      storey_name: storeyName.trim() || null,
      control_points: points.map((point, index) => ({
        pdf: { x: point.x, y: point.y },
        ifc: {
          x: numeric(pairs[index].ifcX),
          y: numeric(pairs[index].ifcY),
        },
      })),
    });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl border border-[#e5e8ec] bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-[#e5e8ec] px-4 py-3">
          <h2 className="text-sm font-semibold text-[#14171c]">
            {t("drawings.viewer.align_3d_title")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("Cancel")}
            className="grid h-8 w-8 place-items-center rounded-lg text-[#59616d] hover:bg-[#eef0f3]"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-4 py-4 text-[13px] text-[#59616d]">
          <p>{t("drawings.viewer.align_3d_help")}</p>

          {points.map((point, index) => (
            <div
              key={`${point.x}-${point.y}-${index}`}
              className="rounded-lg border border-[#e5e8ec] p-3"
            >
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-[0.05em] text-[#969da8]">
                  {t("drawings.viewer.align_3d_point", { number: index + 1 })}
                </span>
                <span className="font-mono text-[11px] text-[#14171c]">
                  PDF {Math.round(point.x)} / {Math.round(point.y)}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="mb-1 block text-[11px] font-semibold text-[#59616d]">
                    {t("drawings.viewer.align_3d_ifc_x")}
                  </span>
                  <input
                    type="number"
                    step="0.001"
                    value={pairs[index]?.ifcX ?? ""}
                    onChange={(event) => setPair(index, "ifcX", event.target.value)}
                    className="w-full rounded-lg border border-[#e5e8ec] px-3 py-2 text-[#14171c] outline-none focus:border-[#2b6ef0]"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-[11px] font-semibold text-[#59616d]">
                    {t("drawings.viewer.align_3d_ifc_y")}
                  </span>
                  <input
                    type="number"
                    step="0.001"
                    value={pairs[index]?.ifcY ?? ""}
                    onChange={(event) => setPair(index, "ifcY", event.target.value)}
                    className="w-full rounded-lg border border-[#e5e8ec] px-3 py-2 text-[#14171c] outline-none focus:border-[#2b6ef0]"
                  />
                </label>
              </div>
            </div>
          ))}

          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="mb-1 block text-[11px] font-semibold text-[#59616d]">
                {t("drawings.viewer.align_3d_z_offset")}
              </span>
              <input
                type="number"
                step="0.01"
                value={zOffset}
                onChange={(event) => setZOffset(event.target.value)}
                className="w-full rounded-lg border border-[#e5e8ec] px-3 py-2 text-[#14171c] outline-none focus:border-[#2b6ef0]"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-semibold text-[#59616d]">
                {t("drawings.viewer.align_3d_storey")}
              </span>
              <input
                type="text"
                value={storeyName}
                onChange={(event) => setStoreyName(event.target.value)}
                placeholder="1. etasje"
                className="w-full rounded-lg border border-[#e5e8ec] px-3 py-2 text-[#14171c] outline-none focus:border-[#2b6ef0]"
              />
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-[#e5e8ec] px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-[#e5e8ec] px-3 py-2 text-sm font-semibold text-[#14171c] hover:bg-[#f6f7f9]"
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            disabled={!complete || saving}
            onClick={handleSave}
            className="rounded-lg bg-[#2b6ef0] px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {saving ? t("Saving...") : t("drawings.viewer.align_3d_save")}
          </button>
        </div>
      </div>
    </div>
  );
};
